import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { organisationsApi } from '../lib/api';
import AppShell from '../components/AppShell';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Loader2, ArrowLeft } from 'lucide-react';
import { describeSuggestion, CONFIRM_LABEL, groupSuggestionsBySection } from '../lib/suggestions';

export const Suggestions = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // `?org=<id>` (from a dashboard row) narrows the page to one organisation.
  const onlyOrgId = searchParams.get('org');
  const [organisations, setOrganisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [showClearAll, setShowClearAll] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);

  const load = async () => {
    try {
      const res = await organisationsApi.getAll();
      setOrganisations(res.data.organisations || []);
    } catch {
      toast.error('Your suggestions could not be loaded.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const orgById = useMemo(() => {
    const map = {};
    for (const o of organisations) map[o.id] = o;
    return map;
  }, [organisations]);

  const groups = useMemo(() => (
    organisations
      .filter((org) => !onlyOrgId || org.id === onlyOrgId)
      .map((org) => ({ org, suggestions: org.pending_suggestions || [] }))
      .filter((g) => g.suggestions.length > 0)
  ), [organisations, onlyOrgId]);

  const totalCount = groups.reduce((sum, g) => sum + g.suggestions.length, 0);
  const onlyOrg = onlyOrgId ? orgById[onlyOrgId] : null;

  const handleConfirm = async (orgId, suggestionId, options = {}) => {
    setSavingId(suggestionId);
    try {
      await organisationsApi.confirmSuggestion(orgId, suggestionId, options);
      await load();
      toast.success('Added.');
    } catch {
      toast.error('That could not be applied.');
    } finally {
      setSavingId(null);
    }
  };

  const handleReject = async (orgId, suggestionId) => {
    setSavingId(suggestionId);
    try {
      await organisationsApi.rejectSuggestion(orgId, suggestionId);
      await load();
    } catch {
      toast.error('That could not be dismissed.');
    } finally {
      setSavingId(null);
    }
  };

  const handleClearAll = async () => {
    setClearingAll(true);
    try {
      await organisationsApi.clearAllSuggestions();
      await load();
      setShowClearAll(false);
      toast.success('All suggestions cleared.');
    } catch {
      toast.error('That could not be cleared — try again in a moment.');
    } finally {
      setClearingAll(false);
    }
  };

  return (
    <AppShell width="narrow">
      <main>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/dashboard')}
          className="mb-4 -ml-2"
          data-testid="back-to-dashboard-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Back to overview
        </Button>

        <PageHeader
          title="Suggested updates"
          lede="Found while reading your documents — nothing here changes anything until you confirm it."
          actions={totalCount > 0 && !onlyOrgId && (
            <Button
              variant="ghost"
              size="sm"
              className="hover:text-breach-ink"
              onClick={() => setShowClearAll(true)}
              data-testid="clear-all-suggestions-btn"
            >
              Ignore all
            </Button>
          )}
        >
          {onlyOrgId && (
            <p className="mt-2 text-[15px] text-ink-55" data-testid="suggestions-org-filter">
              Showing {onlyOrg?.name || 'one organisation'} only.{' '}
              <Link to="/suggestions" className="font-medium text-brand hover:underline underline-offset-4">
                See every organisation
              </Link>
            </p>
          )}
        </PageHeader>

        {loading ? (
          <div className="flex items-center gap-2 text-ink-55 py-10">
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Loading…
          </div>
        ) : totalCount === 0 ? (
          <div className="py-16 text-center rounded-lg bg-ground">
            <p className="font-heading text-[26px] font-semibold tracking-[-0.02em] text-ink mb-1">
              Nothing to review
            </p>
            <p className="text-base text-ink-55">
              When analysis finds something worth adding, it'll show up here.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {groups.map(({ org, suggestions }) => (
              <Card key={org.id} data-testid={`suggestion-group-${org.id}`}>
                <CardHeader>
                  <CardTitle className="font-heading text-[19px] font-semibold tracking-[-0.02em]">
                    {org.name}
                  </CardTitle>
                  <CardDescription className="text-ink-55">
                    {suggestions.length} suggestion{suggestions.length === 1 ? '' : 's'} from documents in this organisation
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  {groupSuggestionsBySection(suggestions, org).map((section) => (
                    <section key={section.key} className="space-y-2.5" data-testid={`suggestion-section-${org.id}-${section.key}`}>
                      <h3 className="pd-eyebrow text-ink-55">{section.title}</h3>
                      {section.suggestions.map((s) => {
                        const { title, detail } = describeSuggestion(s, org, orgById);
                        const isSaving = savingId === s.id;
                        return (
                          <div
                            key={s.id}
                            className="flex items-center justify-between gap-3 rounded-lg bg-ground px-4 py-3"
                            data-testid={`suggestion-row-${s.id}`}
                          >
                            <div className="min-w-0">
                              <p className="text-[15px] font-medium text-ink">{title}</p>
                              {detail && <p className="text-sm text-ink-55">{detail}</p>}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={isSaving}
                                onClick={() => handleReject(org.id, s.id)}
                                data-testid={`reject-suggestion-${s.id}`}
                              >
                                Ignore
                              </Button>
                              <Button
                                size="sm"
                                variant="secondary"
                                disabled={isSaving}
                                onClick={() => handleConfirm(org.id, s.id)}
                                data-testid={`confirm-suggestion-${s.id}`}
                              >
                                {CONFIRM_LABEL[s.type] || 'Confirm'}
                              </Button>
                              {s.can_be_organisation && (
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  disabled={isSaving}
                                  onClick={() => handleConfirm(org.id, s.id, { alsoOrganisation: true })}
                                  data-testid={`confirm-suggestion-with-org-${s.id}`}
                                >
                                  Add placement and organisation
                                </Button>
                              )}
                            </div>
                          </div>
                        );
                  })}
                    </section>
                  ))}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      <AlertDialog open={showClearAll} onOpenChange={setShowClearAll}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-[22px] font-semibold tracking-[-0.02em]">
              Ignore all suggestions?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-ink-70">
              This dismisses every suggestion across every organisation — {totalCount} in total. None of them get
              applied. New suggestions from future analysis runs aren't affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClearAll}
              disabled={clearingAll}
              className="bg-breach-wash text-breach hover:bg-[#F6D2CA]"
              data-testid="confirm-clear-all-btn"
            >
              Ignore all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
};

export default Suggestions;
